import * as React from "react";
import axios from "axios";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "./alert-dialog";
import { Button } from "./button";
import { Text } from "./typography";
import { API_BASE_URL } from '../../lib/api';

/**
 * DeleteHoldingDialog - Confirmation dialog for deleting a holding
 *
 * Uses AlertDialog pattern which prevents accidental closure by
 * clicking outside the dialog. Requires explicit cancel or confirm action.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSuccess - Callback when holding is deleted successfully
 * @param {Object} props.holding - The holding data to delete (requires id and name)
 */
export function DeleteHoldingDialog({
  open,
  onOpenChange,
  onSuccess,
  holding,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset error when dialog closes
  React.useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    // Support both 'holdingId' (from API) and 'id' (for compatibility)
    const holdingId = holding?.holdingId || holding?.id;
    if (!holdingId) {
      setError("No holding ID provided");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(`${API_BASE_URL}/holdings/${holdingId}`, {
        withCredentials: true,
      });

      // Call success callback and close dialog
      if (onSuccess) {
        onSuccess(holdingId);
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      // Handle case where error is an object
      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to delete holding:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const holdingName = holding?.name || holding?.assetName || "this holding";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <AlertDialogTitle>Delete Holding</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            Are you sure you want to delete <strong>{holdingName}</strong>?
            This action cannot be undone and all data associated with this
            holding will be permanently removed.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive p-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Holding"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default DeleteHoldingDialog;
